import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Loader2, Clock } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { formatDateFull } from '../../utils/timeFormat';

// Render a UTC ISO string in the shift's own timezone with a clear tz label.
const localTime = (iso, tz) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz || 'America/New_York', hour: 'numeric', minute: '2-digit',
      timeZoneName: 'short', month: 'short', day: 'numeric',
    }).format(new Date(iso));
  } catch { return new Date(iso).toLocaleString(); }
};

export default function AttendanceCorrections() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [data, setData] = useState({ requests: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState('');
  const [talentMsg, setTalentMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async (which = tab) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/attendance-corrections?status=${which}`);
      setData(res.data);
    } catch (e) {
      toast.error('Could not load attendance corrections.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const openDetail = (r) => { setActive(r); setNote(''); setTalentMsg(''); };

  const act = async (kind) => {
    setSubmitting(true);
    try {
      const payload = {};
      if (note.trim()) payload.review_note = note.trim();
      if (talentMsg.trim()) payload.talent_message = talentMsg.trim();
      const res = await api.post(`/admin/attendance-corrections/${active.id}/${kind}`, payload);
      if (kind === 'approve') {
        const rc = res.data?.recalculated;
        toast.success(`Approved. Recalculated to ${rc?.hours_worked ?? '—'} hrs / $${rc?.total_pay ?? '—'}.`);
      } else {
        toast.success('Request rejected. The talent was notified.');
      }
      setActive(null);
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-attendance-corrections-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} className="text-cyan-600">
            <ArrowLeft className="w-4 h-4 mr-2" /> Admin Home
          </Button>
          <h1 className="text-lg font-bold text-gray-900">Attendance Corrections</h1>
          <div className="w-24" />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 mb-4">
          {['pending', 'approved', 'rejected'].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`corrections-tab-${t}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${tab === t ? 'bg-cyan-600 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}>
              {t} {t === 'pending' && data.summary?.pending ? `(${data.summary.pending})` : ''}
            </button>
          ))}
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader><CardTitle className="text-gray-900 text-base capitalize">{tab} requests</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
            ) : data.requests.length === 0 ? (
              <div className="p-8 text-center text-gray-400" data-testid="corrections-empty">No {tab} requests.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {data.requests.map((r) => (
                  <button key={r.id} onClick={() => openDetail(r)} data-testid={`correction-row-${r.id}`}
                    className="w-full text-left p-4 hover:bg-slate-50 transition">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-900">{r.talent_name} · {r.correction_type.replace('_', ' ')}</p>
                        <p className="text-sm text-gray-500">{r.gig_name} • {r.date_worked ? formatDateFull(r.date_worked) : ''}</p>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{r.reason}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        {r.discrepancy_minutes != null && (
                          <p className="text-xs font-semibold text-amber-600">{r.discrepancy_minutes} min off</p>
                        )}
                        <p className="text-[11px] text-gray-400 mt-1">{localTime(r.original_clock_in, r.timezone)}</p>
                        <p className="text-[11px] text-cyan-600">→ {localTime(r.proposed_clock_in, r.timezone)}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg" data-testid="correction-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-cyan-600" /> Review Attendance Correction</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">{active?.talent_name} • {active?.gig_name}</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-[11px] uppercase text-slate-400 font-semibold">Recorded clock-in</p>
                  <p className="text-sm font-medium text-slate-800 mt-1" data-testid="correction-original">{localTime(active.original_clock_in, active.timezone)}</p>
                </div>
                <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3">
                  <p className="text-[11px] uppercase text-cyan-600 font-semibold">Proposed clock-in</p>
                  <p className="text-sm font-medium text-cyan-800 mt-1" data-testid="correction-proposed">{localTime(active.proposed_clock_in, active.timezone)}</p>
                </div>
              </div>
              {active.discrepancy_minutes != null && (
                <p className="text-sm text-amber-700">Discrepancy: <strong>{active.discrepancy_minutes} minutes</strong></p>
              )}
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-[11px] uppercase text-slate-400 font-semibold mb-1">Reason</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{active.reason}</p>
                {active.evidence_source && <p className="text-xs text-slate-400 mt-1">Source: {active.evidence_source}{active.incident_ref ? ` · ${active.incident_ref}` : ''}</p>}
              </div>
              {active.status === 'pending' ? (
                <div className="space-y-3 border-t border-slate-100 pt-3">
                  <div>
                    <label className="text-xs text-slate-600">Internal note (admin only)</label>
                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="correction-note" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Message to talent (optional)</label>
                    <Textarea rows={2} value={talentMsg} onChange={(e) => setTalentMsg(e.target.value)} data-testid="correction-talent-msg" />
                  </div>
                  <p className="text-xs text-slate-400">Approving sets the corrected clock-in, recomputes hours &amp; pay, and preserves the original timestamp.</p>
                </div>
              ) : (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                  <p className="text-slate-700 capitalize"><span className="font-medium">Status:</span> {active.status}</p>
                  {active.review_note && <p className="text-slate-600 mt-1">Note: {active.review_note}</p>}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActive(null)}>Close</Button>
            {active && active.status === 'pending' && (
              <>
                <Button variant="outline" className="text-red-600 border-red-200" disabled={submitting} onClick={() => act('reject')} data-testid="correction-reject-btn">Reject</Button>
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" disabled={submitting} onClick={() => act('approve')} data-testid="correction-approve-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Approve &amp; recalc
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
